import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import MenuGroup from "aldoo-ra/MenuGroup"
import Typography from "aldoo-ra/Typography"
import Localization from "aldoo-ra/Localization"
import Icon from "aldoo-ra/Icon"

const SideMenuDesktop = ({ platform, controller }) => {
  const { template } = Localization()

  // Destructure controller values
  const {
    getFeaturesForGroup,
    selectFeatureByID,
    selectedGroup,
    setSelectedGroup,
    groups,
    GROUP_TO_URL,
    URL_TO_GROUP,
  } = controller

  // State management
  const [activeFeature, setActiveFeature] = useState(null)
  const initializedRef = useRef(false)

  // Enhanced URL-based group selection, runs once on mount
  useEffect(() => {
    if (typeof window === "undefined") return
    if (initializedRef.current) return

    // Set this flag immediately to prevent multiple executions
    initializedRef.current = true

    // Delay to ensure controller is fully initialized
    const initializeFromUrl = () => {
      try {
        const currentPath = window.location.pathname
        // Extract the URL segment that should match a group
        let urlSegment
        if (currentPath.includes("/features/")) {
          // Get the segment after /features/
          const featuresIndex = currentPath.indexOf("/features/")
          const pathAfterFeatures = currentPath.substring(featuresIndex + 10) // +10 for '/features/'
          urlSegment = pathAfterFeatures.split("/")[0] // Get first segment after features
        } else {
          // Fallback to just the last segment
          urlSegment = currentPath.split("/").pop()
        }

        const groupFromUrl = URL_TO_GROUP[urlSegment]

        // Check if the URL corresponds to a valid group
        if (groupFromUrl && groups.includes(groupFromUrl)) {
          // Check if we're already showing this group to avoid loops
          if (selectedGroup !== groupFromUrl) {
            setSelectedGroup(groupFromUrl)
          }

          // Get possible feature ID from URL
          const pathParts = currentPath.split("/")
          const maybeFeatureId =
            pathParts.length > 1 ? pathParts[pathParts.length - 1] : null

          // Select feature
          const groupFeatures = getFeaturesForGroup(groupFromUrl)

          if (groupFeatures && groupFeatures.length > 0) {
            // Try to find the feature from URL if it exists
            if (maybeFeatureId && maybeFeatureId !== urlSegment) {
              const featureById = groupFeatures.find(
                (f) => f && f.id === maybeFeatureId
              )

              if (featureById) {
                if (activeFeature !== featureById.id) {
                  selectFeatureByID(featureById.id)
                  setActiveFeature(featureById.id)
                }
                return
              }
            }

            // Fall back to first feature if no matching feature ID found
            // Only do this if no feature is currently selected or if the active feature doesn't match
            if (
              !activeFeature ||
              !groupFeatures.some((f) => f && f.id === activeFeature)
            ) {
              selectFeatureByID(groupFeatures[0].id)
              setActiveFeature(groupFeatures[0].id)
            }
          }
        } else {
          // Only fall back to first group if NO group is currently selected
          if (groups && groups.length > 0 && !selectedGroup) {
            setSelectedGroup(groups[0])

            const firstGroupFeatures = getFeaturesForGroup(groups[0])
            if (firstGroupFeatures && firstGroupFeatures.length > 0) {
              selectFeatureByID(firstGroupFeatures[0].id)
              setActiveFeature(firstGroupFeatures[0].id)
            }
          }
        }
      } catch (error) {
        console.error("Error initializing from URL:", error)
      }
    }

    // Delay initialization slightly to ensure everything is loaded
    setTimeout(initializeFromUrl, 100)

    // Cleanup on unmount
    return () => {
      initializedRef.current = false
    }
  }, [])

  // Memoized menu items to prevent unnecessary re-renders
  const menuItems = useMemo(() => {
    if (!platform) return []
    // Create menu items for all groups
    return groups.map((group) => {
      const urlSegment = GROUP_TO_URL[group]
      // Include all features for each group, they'll be filtered by preProcessItem
      const groupFeatures = getFeaturesForGroup(group)

      return {
        label: group
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        link: [{ url: `/features/${urlSegment}` }],
        items: groupFeatures,
      }
    })
  }, [groups, platform, getFeaturesForGroup, GROUP_TO_URL])

  // Handle menu item click
  const handleItemClick = (e, item) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      const urlPath = item.link[0].url.split("/").pop()
      const newGroup = URL_TO_GROUP[urlPath]

      if (newGroup && newGroup !== selectedGroup) {
        // Update group
        setSelectedGroup(newGroup)

        // Select first feature of the group
        const groupFeatures = getFeaturesForGroup(newGroup)
        if (groupFeatures && groupFeatures.length > 0) {
          selectFeatureByID(groupFeatures[0].id)
          setActiveFeature(groupFeatures[0].id)
        }
      }
    } catch (error) {
      console.error("Error handling item click:", error)
    }
  }

  // We don't need a separate feature click handler as in the original layout

  // We don't need to calculate selectedGroupFeatures as we're not showing them separately

  // Render guard
  if (!platform || !menuItems.length) return null

  return (
    <div className="absolute right-0 top-[145px] transform mr-[60px] z-50">
      {/* Group Menu */}
      <MenuGroup
        name="Header Features"
        renderAs="vlist"
        styleOverrides={{
          menu_item: "my-0 py-0",
        }}
        items={menuItems}
        preProcessItem={(item) => {
          if (!item.items) return item

          // Filter and process items for platform compatibility
          item.items = item.items
            .filter((subItem) => {
              if (!subItem) return false
              const platforms = subItem.options?.platforms
              if (!platforms) return true
              return (
                typeof platforms === "string" &&
                platforms.toLowerCase().includes(platform)
              )
            })
            .map((i) => {
              if (i.link && i.link[0]) {
                i.link[0].url = template(i.link[0].url, { platform })
              }
              return i
            })
          return item
        }}
        menuItemRender={(item) => {
          // Determine if this is a feature item from a group
          const isFeatureItem =
            item.id && (!item.link || !item.link[0]?.url.includes("/features/"))

          if (isFeatureItem) {
            // This is a feature item, handle it accordingly
            const isActive = item.id === activeFeature

            return (
              <div className="flex flex-row justify-end items-center relative gap-3 py-0">
                <a
                  href={(item.link && item.link[0] && item.link[0].url) || "#"}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (item.id) {
                      selectFeatureByID(item.id)
                      setActiveFeature(item.id)
                    }
                  }}
                >
                  <Typography
                    variant={isActive ? "body_18_accent" : "body_16"}
                    className={isActive ? "text-primary" : "text-gray_3"}
                  >
                    {item.label}
                  </Typography>
                </a>
                {isActive && (
                  <div className="absolute pl-2 -right-24 w-24 text-primary">
                    <Icon id="arrow_feature" className="w-[28px] h-auto" />
                  </div>
                )}
              </div>
            )
          } else {
            // This is a group item
            const url = item.link && item.link[0] && item.link[0].url
            const urlSegment = url ? url.split("/").pop() : ''
            const isSelected = selectedGroup === URL_TO_GROUP[urlSegment]

            return (
              <div className="flex flex-row justify-end items-center relative gap-3 py-0">
                <a
                  href={url ? url.replace("{{platform}}", platform) : '#'}
                  onClick={(e) => handleItemClick(e, item)}
                >
                  <Typography
                    variant={isSelected ? "body_24_accent" : "body_20"}
                    className={isSelected ? "text-primary" : "text-gray_2"}
                  >
                    {item.label}
                  </Typography>
                </a>
                {isSelected && (
                  <div className="absolute pl-2 -right-40 w-40 text-primary">
                    <Icon id="arrow_menu_desktop" className="w-[54px] h-auto" />
                  </div>
                )}
              </div>
            )
          }
        }}
        itemWidth="auto"
        skipRootItem
      />
    </div>
  )
}

export default SideMenuDesktop
